import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { Switch } from "../ui/switch";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../ui/select";
import { Textarea } from "../ui/textarea";
import { api } from "../../lib/api";
import { ADMIN } from "../../constants/testIds";
import { toast } from "../ui/sonner";

const ART_LAYERS = ["bg", "fg", "subject", "cloud"];

function titlesToText(titles) {
  return (titles || []).map((t) => `${t.t} | ${t.a}`).join("\n");
}
function textToTitles(text) {
  return text.split("\n").map((l) => l.trim()).filter(Boolean).map((line) => {
    const [t, a] = line.split("|").map((s) => (s || "").trim());
    return { t: t || "Untitled", a: a || "" };
  });
}

export const ReleaseEditorModal = ({ release, onClose, onSaved }) => {
  const [form, setForm] = useState({
    id: release?.id || null,
    slug: release?.slug || "",
    title: release?.title || "",
    artist: release?.artist || "",
    status: release?.status || "draft",
    sleepControls: release?.featureFlags?.sleepControls || false,
    youtubePlaylistId: release?.track?.youtubePlaylistId || "",
    spotifyUrl: release?.track?.spotifyUrl || "",
    ytMusicUrl: release?.track?.ytMusicUrl || "",
    titlesText: titlesToText(release?.track?.titles),
    art: {
      bg: release?.art?.bg || "", fg: release?.art?.fg || "",
      subject: release?.art?.subject || "", cloud: release?.art?.cloud || "",
    },
    theme: {
      moon: release?.theme?.moon || "#dfe6fa",
      moonSoft: release?.theme?.moonSoft || "#f2f4ff",
      lanternC1: release?.theme?.lanternC1 || "rgba(255,228,158,1)",
      glowC1: release?.theme?.glowC1 || "rgba(255,231,172,.95)",
    },
  });
  const [saving, setSaving] = useState(false);
  const [artPrompt, setArtPrompt] = useState("");
  const [artLayer, setArtLayer] = useState("bg");
  const [generating, setGenerating] = useState(false);

  const update = (field, value) => setForm((f) => ({ ...f, [field]: value }));
  const updateArt = (layer, value) => setForm((f) => ({ ...f, art: { ...f.art, [layer]: value } }));
  const updateTheme = (key, value) => setForm((f) => ({ ...f, theme: { ...f.theme, [key]: value } }));

  const buildPayload = () => ({
    slug: form.slug, title: form.title, artist: form.artist, status: form.status,
    art: form.art,
    theme: form.theme,
    track: {
      titles: textToTitles(form.titlesText),
      youtubePlaylistId: form.youtubePlaylistId,
      spotifyUrl: form.spotifyUrl,
      ytMusicUrl: form.ytMusicUrl,
    },
    featureFlags: { sleepControls: form.sleepControls },
  });

  const onSave = async () => {
    setSaving(true);
    try {
      if (form.id) {
        await api.put(`/admin/releases/${form.id}`, buildPayload());
      } else {
        const { data } = await api.post("/admin/releases", buildPayload());
        setForm((f) => ({ ...f, id: data.id }));
      }
      toast.success("Release saved successfully");
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail?.toString() || "Failed to save release");
    } finally {
      setSaving(false);
    }
  };

  const onGenerateArt = async () => {
    if (!form.id) {
      toast.error("Save the release as a draft first, then generate art layers.");
      return;
    }
    if (!artPrompt) {
      toast.error("Describe what this art layer should look like");
      return;
    }
    setGenerating(true);
    try {
      const { data } = await api.post(`/admin/releases/${form.id}/generate-art`, { layer: artLayer, prompt: artPrompt });
      updateArt(artLayer, `${process.env.REACT_APP_BACKEND_URL}${data.url}`);
      toast.success(`AI art layer generated for "${artLayer}"`);
    } catch (e) {
      toast.error("Art generation failed, try a different prompt");
    } finally {
      setGenerating(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent data-testid={ADMIN.releaseEditorModal} className="bg-slate-950 border-white/10 text-slate-200 max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-amber-100/90">{form.id ? "Edit Release" : "New Release"}</DialogTitle>
          <DialogDescription className="text-slate-500">Configure art layers, track metadata and streaming links for this release.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Input data-testid={ADMIN.releaseTitleInput} placeholder="Title" value={form.title} onChange={(e) => update("title", e.target.value)} className="bg-slate-900 border-white/10" />
            <Input data-testid={ADMIN.releaseArtistInput} placeholder="Artist" value={form.artist} onChange={(e) => update("artist", e.target.value)} className="bg-slate-900 border-white/10" />
          </div>
          <Input data-testid={ADMIN.releaseSlugInput} placeholder="slug-like-this" value={form.slug} onChange={(e) => update("slug", e.target.value)} className="bg-slate-900 border-white/10" />

          <div className="flex items-center gap-4">
            <Select value={form.status} onValueChange={(v) => update("status", v)}>
              <SelectTrigger data-testid={ADMIN.statusSelect} className="w-40 bg-slate-900 border-white/10"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-slate-900 border-white/10 text-slate-200">
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="archived">Archived</SelectItem>
              </SelectContent>
            </Select>
            <div className="flex items-center gap-2">
              <Switch data-testid={ADMIN.sleepControlsToggle} checked={form.sleepControls} onCheckedChange={(v) => update("sleepControls", v)} />
              <span className="text-sm text-slate-400">Sleep controls</span>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-3">
            <Input placeholder="YouTube playlist ID" value={form.youtubePlaylistId} onChange={(e) => update("youtubePlaylistId", e.target.value)} className="bg-slate-900 border-white/10" />
            <Input placeholder="Spotify playlist URL" value={form.spotifyUrl} onChange={(e) => update("spotifyUrl", e.target.value)} className="bg-slate-900 border-white/10" />
            <Input placeholder="YouTube Music URL" value={form.ytMusicUrl} onChange={(e) => update("ytMusicUrl", e.target.value)} className="bg-slate-900 border-white/10" />
          </div>

          <div>
            <label className="text-xs uppercase tracking-widest text-amber-300/80 font-mono">Track Titles (one per line — "Title | Artist")</label>
            <Textarea value={form.titlesText} onChange={(e) => update("titlesText", e.target.value)} rows={4} className="bg-slate-900 border-white/10 mt-1" />
          </div>

          <div className="space-y-2">
            <label className="text-xs uppercase tracking-widest text-amber-300/80 font-mono">Art Layers</label>
            {ART_LAYERS.map((layer) => (
              <div key={layer} className="flex items-center gap-2">
                <span className="text-xs text-slate-500 w-16 capitalize">{layer}</span>
                <Input value={form.art[layer]} onChange={(e) => updateArt(layer, e.target.value)} placeholder={`${layer} image URL`} className="bg-slate-900 border-white/10" />
              </div>
            ))}
            <div className="flex items-center gap-2 pt-2">
              <Select value={artLayer} onValueChange={setArtLayer}>
                <SelectTrigger className="w-32 bg-slate-900 border-white/10"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-slate-900 border-white/10 text-slate-200">
                  {ART_LAYERS.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
              <Input value={artPrompt} onChange={(e) => setArtPrompt(e.target.value)} placeholder="Describe the AI art for this layer" className="bg-slate-900 border-white/10" />
              <Button data-testid={ADMIN.generateArtButton} type="button" disabled={generating} onClick={onGenerateArt} className="bg-cyan-500 hover:bg-cyan-400 text-slate-950 shrink-0">
                {generating ? "Generating…" : "Generate"}
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Input value={form.theme.moon} onChange={(e) => updateTheme("moon", e.target.value)} placeholder="Moon color" className="bg-slate-900 border-white/10" />
            <Input value={form.theme.lanternC1} onChange={(e) => updateTheme("lanternC1", e.target.value)} placeholder="Lantern glow color" className="bg-slate-900 border-white/10" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="border-white/10 text-slate-300" onClick={onClose}>Cancel</Button>
          <Button data-testid={ADMIN.saveReleaseButton} disabled={saving} onClick={onSave} className="bg-amber-500 hover:bg-amber-400 text-slate-950">
            {saving ? "Saving…" : "Save Release"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
